import React, { createContext, useContext, useState, useCallback } from 'react';
import campaignApi from '../services/campaignApi';

// Initialize campaign context
const CampaignContext = createContext({});

/**
 * Campaign Provider
 * Provides campaign state and operations to components
 */
export const CampaignProvider = ({ children }) => {
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentCampaign, setCurrentCampaign] = useState(null);
    const [campaignContacts, setCampaignContacts] = useState([]);
    const [campaignReport, setCampaignReport] = useState(null);

    // Get all campaigns with pagination and filters
    const fetchCampaigns = useCallback(async (params = {}) => {
        setLoading(true);
        setError(null);
        try {
            const response = await campaignApi.getCampaigns(params);
            setCampaigns(response.campaigns);
            return response;
        } catch (err) {
            setError(err.message || 'Failed to fetch campaigns');
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch a single campaign by ID
    const fetchCampaign = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const response = await campaignApi.getCampaign(id);
            setCurrentCampaign(response.campaign);
            return response.campaign;
        } catch (err) {
            setError(err.message || 'Failed to fetch campaign');
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    // Create a new campaign
    const createCampaign = useCallback(async (campaignData) => {
        setLoading(true);
        setError(null);
        try {
            const response = await campaignApi.createCampaign(campaignData);
            setCampaigns((prevCampaigns) => [response.campaign, ...prevCampaigns]);
            return response.campaign;
        } catch (err) {
            setError(err.message || 'Failed to create campaign');
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    // Update an existing campaign
    const updateCampaign = useCallback(async (id, data) => {
        setLoading(true);
        setError(null);
        try {
            const response = await campaignApi.updateCampaign(id, data);
            setCampaigns((prevCampaigns) =>
                prevCampaigns.map((campaign) =>
                    campaign._id === id ? response.campaign : campaign
                )
            );
            if (currentCampaign && currentCampaign._id === id) {
                setCurrentCampaign(response.campaign);
            }
            return response.campaign;
        } catch (err) {
            setError(err.message || 'Failed to update campaign');
            return null;
        } finally {
            setLoading(false);
        }
    }, [currentCampaign]);

    // Delete a campaign
    const deleteCampaign = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const response = await campaignApi.deleteCampaign(id);
            setCampaigns((prevCampaigns) =>
                prevCampaigns.filter((campaign) => campaign._id !== id)
            );
            if (currentCampaign && currentCampaign._id === id) {
                setCurrentCampaign(null);
            }
            return response;
        } catch (err) {
            setError(err.message || 'Failed to delete campaign');
            return null;
        } finally {
            setLoading(false);
        }
    }, [currentCampaign]);

    // Duplicate a campaign
    const duplicateCampaign = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const response = await campaignApi.duplicateCampaign(id);
            setCampaigns((prevCampaigns) => [response.campaign, ...prevCampaigns]);
            return response.campaign;
        } catch (err) {
            setError(err.message || 'Failed to duplicate campaign');
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    // Start a campaign
    const startCampaign = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const response = await campaignApi.startCampaign(id);
            fetchCampaign(id); // Refresh campaign data after starting
            return response;
        } catch (err) {
            setError(err.message || 'Failed to start campaign');
            return null;
        } finally {
            setLoading(false);
        }
    }, [fetchCampaign]);

    // Schedule a campaign
    const scheduleCampaign = useCallback(async (id, scheduleTime) => {
        setLoading(true);
        setError(null);
        try {
            const response = await campaignApi.scheduleCampaign(id, scheduleTime);
            fetchCampaign(id); // Refresh campaign data after scheduling
            return response;
        } catch (err) {
            setError(err.message || 'Failed to schedule campaign');
            return null;
        } finally {
            setLoading(false);
        }
    }, [fetchCampaign]);

    // Cancel scheduled campaign
    const cancelSchedule = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const response = await campaignApi.cancelScheduledCampaign(id);
            fetchCampaign(id); // Refresh campaign data after cancelling schedule
            return response;
        } catch (err) {
            setError(err.message || 'Failed to cancel scheduled campaign');
            return null;
        } finally {
            setLoading(false);
        }
    }, [fetchCampaign]);

    // Pause a campaign
    const pauseCampaign = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const response = await campaignApi.pauseCampaign(id);
            fetchCampaign(id); // Refresh campaign data after pausing
            return response;
        } catch (err) {
            setError(err.message || 'Failed to pause campaign');
            return null;
        } finally {
            setLoading(false);
        }
    }, [fetchCampaign]);

    // Resume a campaign
    const resumeCampaign = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const response = await campaignApi.resumeCampaign(id);
            fetchCampaign(id); // Refresh campaign data after resuming
            return response;
        } catch (err) {
            setError(err.message || 'Failed to resume campaign');
            return null;
        } finally {
            setLoading(false);
        }
    }, [fetchCampaign]);

    // Cancel a campaign
    const cancelCampaign = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const response = await campaignApi.cancelCampaign(id);
            fetchCampaign(id); // Refresh campaign data after cancelling
            return response;
        } catch (err) {
            setError(err.message || 'Failed to cancel campaign');
            return null;
        } finally {
            setLoading(false);
        }
    }, [fetchCampaign]);

    // Get campaign status
    const getCampaignStatus = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const response = await campaignApi.getCampaignStatus(id);

            // If we have a status response and a current campaign loaded
            if (response.status && currentCampaign && currentCampaign._id === id) {
                // Update the current campaign with the new status if it changed
                if (response.status.status !== currentCampaign.status) {
                    setCurrentCampaign(prev => ({
                        ...prev,
                        status: response.status.status
                    }));
                }
            }

            return response.status;
        } catch (err) {
            setError(err.message || 'Failed to get campaign status');
            return null;
        } finally {
            setLoading(false);
        }
    }, [currentCampaign]);

    // Fetch contacts for a campaign
    const fetchCampaignContacts = useCallback(async (id, params = {}) => {
        setLoading(true);
        setError(null);
        try {
            const response = await campaignApi.getCampaignContacts(id, params);
            setCampaignContacts(response.contacts);
            return response;
        } catch (err) {
            setError(err.message || 'Failed to fetch campaign contacts');
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    // Add contacts to a campaign
    const addContacts = useCallback(async (id, contacts) => {
        setLoading(true);
        setError(null);
        try {
            const response = await campaignApi.addCampaignContacts(id, contacts);
            fetchCampaignContacts(id); // Refresh contacts after adding
            return response;
        } catch (err) {
            setError(err.message || 'Failed to add contacts');
            return null;
        } finally {
            setLoading(false);
        }
    }, [fetchCampaignContacts]);

    // Upload contacts from file
    const uploadContacts = useCallback(async (id, file, mapping) => {
        setLoading(true);
        setError(null);
        try {
            const response = await campaignApi.uploadCampaignContacts(id, file, mapping);
            fetchCampaignContacts(id); // Refresh contacts after uploading
            return response;
        } catch (err) {
            setError(err.message || 'Failed to upload contacts');
            return null;
        } finally {
            setLoading(false);
        }
    }, [fetchCampaignContacts]);

    // Delete a contact
    const deleteContact = useCallback(async (campaignId, contactId) => {
        setLoading(true);
        setError(null);
        try {
            const response = await campaignApi.deleteCampaignContact(campaignId, contactId);
            setCampaignContacts((prevContacts) =>
                prevContacts.filter((contact) => contact._id !== contactId)
            );
            return response;
        } catch (err) {
            setError(err.message || 'Failed to delete contact');
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    // Delete all contacts
    const deleteAllContacts = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const response = await campaignApi.deleteAllCampaignContacts(id);
            setCampaignContacts([]);
            return response;
        } catch (err) {
            setError(err.message || 'Failed to delete all contacts');
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    // Get campaign report
    const fetchCampaignReport = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const response = await campaignApi.getCampaignReport(id);
            setCampaignReport(response.report);
            return response.report;
        } catch (err) {
            setError(err.message || 'Failed to fetch campaign report');
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    // Regenerate campaign report
    const regenerateReport = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const response = await campaignApi.regenerateReport(id);
            setCampaignReport(response.report);
            return response.report;
        } catch (err) {
            setError(err.message || 'Failed to regenerate report');
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    // Get delivery summary
    const getDeliverySummary = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const response = await campaignApi.getDeliverySummary(id);
            return response.summary;
        } catch (err) {
            setError(err.message || 'Failed to get delivery summary');
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    // Get batch analysis
    const getBatchAnalysis = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const response = await campaignApi.getBatchAnalysis(id);
            return response.analysis;
        } catch (err) {
            setError(err.message || 'Failed to get batch analysis');
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    // Get failure analysis
    const getFailureAnalysis = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const response = await campaignApi.getFailureAnalysis(id);
            return response.analysis;
        } catch (err) {
            setError(err.message || 'Failed to get failure analysis');
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch templates
    const fetchTemplates = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await campaignApi.getTemplates();
            // Return the full response to let components handle the nested structure
            return response.templates;
        } catch (err) {
            setError(err.message || 'Failed to fetch templates');
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    // Get template details
    const getTemplateDetails = useCallback(async (name) => {
        setLoading(true);
        setError(null);
        try {
            const response = await campaignApi.getTemplateDetails(name);
            return response.template;
        } catch (err) {
            setError(err.message || 'Failed to get template details');
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    // Test template rendering
    const testTemplate = useCallback(async (data) => {
        setLoading(true);
        setError(null);
        try {
            const response = await campaignApi.testTemplate(data);
            console.log('Template test API response:', response);
            return response.processedComponents;
        } catch (err) {
            setError(err.message || 'Failed to test template');
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    // Context value
    const value = {
        // State
        campaigns,
        loading,
        error,
        currentCampaign,
        campaignContacts,
        campaignReport,

        // Campaign management
        fetchCampaigns,
        fetchCampaign,
        createCampaign,
        updateCampaign,
        deleteCampaign,
        duplicateCampaign,

        // Campaign execution
        startCampaign,
        scheduleCampaign,
        cancelSchedule,
        pauseCampaign,
        resumeCampaign,
        cancelCampaign,
        getCampaignStatus,

        // Contact management
        fetchCampaignContacts,
        addContacts,
        uploadContacts,
        deleteContact,
        deleteAllContacts,

        // Reporting
        fetchCampaignReport,
        regenerateReport,
        getDeliverySummary,
        getBatchAnalysis,
        getFailureAnalysis,

        // Templates
        fetchTemplates,
        getTemplateDetails,
        testTemplate
    };

    return (
        <CampaignContext.Provider value={value}>
            {children}
        </CampaignContext.Provider>
    );
};

// Custom hook to use the campaign context
export const useCampaign = () => {
    const context = useContext(CampaignContext);
    if (context === undefined) {
        throw new Error('useCampaign must be used within a CampaignProvider');
    }
    return context;
};

export default CampaignContext; 